import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAuth from 'hooks/useAuth'
import { useFeatureFlags } from 'helpers/feature-flag'
import { PRODUCT_SUMMARY_ACTION } from 'helpers/access'
import { getAccessList } from '~/services/user'
import appConfig from 'configs/app'
import { FEATURE_FLAGS } from 'constants/feature-flags'

export const accessListQueryKey = ['access-list']

// Stable reference so consumers' dependency lists don't churn.
const EMPTY_ROLES: string[] = []

// Who the current user is and what they may do — the single hook the rest of
// the app should ask, so screens never read AuthContext or /me directly.
//
// AuthProvider owns the GET /me request and makes it exactly once per session
// (see contexts/AuthContext). This hook reads that off the context and adds the
// `multiple-roles` flag awareness the provider can't supply, since it sits
// outside FlagProvider (see pages/_app.tsx).
//
// Capabilities are deliberately NOT derived from the role name: a member can
// hold extra permissions beyond their role's defaults, so a role -> actions
// lookup would deny what the BE actually grants.
export default function useSession() {
  const {
    user,
    isAuthenticated,
    isInitialized,
    orgId,
    orgName,
    orgRole,
    permissions,
    isLoadingPermissions,
  } = useAuth()
  const isMultipleRolesEnabled = Boolean(
    useFeatureFlags(FEATURE_FLAGS.multipleRoles),
  )

  // The legacy creator-account feature whitelist (GET /hub/v1/role). Fetched
  // here rather than during AuthProvider's bootstrap precisely so it can be
  // gated on the flag: with multiple-roles on, capabilities come from /me and
  // this whitelist is never read, so the request is pure waste.
  const { data: accessList } = useQuery(
    accessListQueryKey,
    () => getAccessList(),
    {
      enabled: isAuthenticated && !isMultipleRolesEnabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  )

  // Product Summary is opt-in per creator rather than returned by the BE.
  //
  // The `!!` is load-bearing: with the env var unset it is undefined, and an
  // account with no creator profile has an undefined creatorId, so without it
  // `undefined === undefined` would grant access to everyone.
  const creatorId = user?.creator?.creator_id
  const hasProductSummaryAccess = Boolean(
    creatorId &&
    (appConfig.whitelistedCreatorProductSummary
      ?.split(',')
      .includes(creatorId) ||
      (!!appConfig.hardcodedProductSummaryCreatorId &&
        creatorId === appConfig.hardcodedProductSummaryCreatorId)),
  )

  // Flag ON: synthesise the action so the nav filter and route guard gate
  // Summary through the machinery they already use for every other entry.
  // Without it the path has no entry in NAV_VIEW_ACTION_BY_PATH, and
  // filterNavByPermissions leaves such paths visible — so Summary showed for
  // every member.
  const grantedActions = useMemo(
    () =>
      hasProductSummaryAccess
        ? [...permissions, PRODUCT_SUMMARY_ACTION]
        : permissions,
    [permissions, hasProductSummaryAccess],
  )

  // Flag OFF: the same opt-in expressed as the legacy access slug.
  const roles = useMemo(() => {
    const access = accessList?.access
    if (!access) return EMPTY_ROLES
    return hasProductSummaryAccess
      ? [...access, 'product-summary']
      : access
  }, [accessList, hasProductSummaryAccess])

  return {
    // Identity
    user,
    isAuthenticated,
    isInitialized,
    orgId,
    orgName,
    role: orgRole,
    // Flag off means no /me and an empty role, so treat the lone creator as
    // Owner — same reasoning as canPerform below.
    isOwner:
      !isMultipleRolesEnabled || orgRole?.toLowerCase() === 'owner',

    // Capabilities
    permissions: grantedActions,
    // Raw grant check — e.g. permission('team:invite'). Action keys are opaque;
    // the prefix isn't reliably the owning section. Use this only where the
    // caller has already handled the flag-off case.
    permission: (action: string): boolean =>
      grantedActions.includes(action),
    // Flag-aware check for gating UI: with multiple-roles off nothing is
    // restricted, so this reports true and screens behave exactly as they do
    // in production today. Prefer this at every hide/disable site.
    canPerform: (action: string): boolean =>
      !isMultipleRolesEnabled || grantedActions.includes(action),
    isLoadingPermissions,

    // Legacy creator-account feature whitelist — only populated while the flag
    // is off. Prefer `permission()` / `canPerform()` for new work.
    roles,
    isMultipleRolesEnabled,
  }
}
